import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import ReactMarkdown from 'react-markdown'
import {
  ArrowLeft,
  Download,
  MessageSquare,
  Check,
  Receipt,
  Clock,
  Calendar,
  AlertCircle,
  Printer
} from 'lucide-react'
import { toast } from 'react-toastify'
import { Sidebar, ConfirmModal } from '@/shared/components'
import { TeamExchangeZone } from '../components'
import { useProjectQuote, useAcceptQuote, useRejectQuote } from '../hooks'
import { useCreateCheckout } from '@/features/payment'
import { cn } from '@/shared/lib/utils'
import type { Project, Team, Quote } from '@/shared/types'

interface ProjectQuotingPageProps {
  project: Project
  team: Team
}

type StepStatus = 'completed' | 'current' | 'upcoming'

interface HorizontalStepProps {
  number: number
  label: string
  status: StepStatus
  isLast?: boolean
}

function HorizontalStep({ number, label, status, isLast }: HorizontalStepProps) {
  return (
    <>
      <div className="flex items-center gap-2">
        {status === 'completed' ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500">
            <Check className="h-3.5 w-3.5 text-white" />
          </div>
        ) : status === 'current' ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent">
            <span className="text-xs font-semibold text-white">{number}</span>
          </div>
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-border bg-background">
            <span className="text-xs font-medium text-foreground-muted">{number}</span>
          </div>
        )}
        <span
          className={cn(
            'text-sm',
            status === 'completed' && 'text-foreground-muted',
            status === 'current' && 'font-medium text-foreground',
            status === 'upcoming' && 'text-foreground-muted'
          )}
        >
          {label}
        </span>
      </div>
      {!isLast && (
        <div
          className={cn(
            'mx-2 h-0.5 w-10',
            status === 'completed' ? 'bg-green-500' : 'bg-border'
          )}
        />
      )}
    </>
  )
}

function formatCurrency(amount: number, currency: string): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  return formatter.format(amount / 100) // Amount is in cents
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

interface RejectQuoteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
  isLoading: boolean
}

function RejectQuoteModal({ isOpen, onClose, onConfirm, isLoading }: RejectQuoteModalProps) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError(t('quoting.rejectReasonRequired'))
      return
    }
    setError('')
    onConfirm(reason)
  }

  const handleClose = () => {
    setReason('')
    setError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold text-foreground">
          {t('quoting.rejectReasonTitle')}
        </h2>
        <p className="mt-1 text-sm text-foreground-muted">
          {t('quoting.rejectReasonDescription')}
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('quoting.rejectReasonPlaceholder')}
          className="mt-4 h-32 w-full resize-none rounded-lg border border-border bg-background p-3 text-sm text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        {error && (
          <p className="mt-2 text-sm text-destructive">{error}</p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {isLoading ? t('common.loading') : t('common.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ProjectQuotingPage({ project, team }: ProjectQuotingPageProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)

  const { data: quote, isLoading: isLoadingQuote } = useProjectQuote(project.id)
  const acceptQuoteMutation = useAcceptQuote()
  const rejectQuoteMutation = useRejectQuote()
  const createCheckoutMutation = useCreateCheckout()

  const handleDownloadPdf = () => {
    if (project.specs_pdf_url) {
      window.open(project.specs_pdf_url, '_blank')
    }
  }

  const handleAcceptQuote = async () => {
    if (!quote) return

    try {
      // First, mark the quote as accepted
      await acceptQuoteMutation.mutateAsync(quote.id)
      setIsAcceptModalOpen(false)

      // Then redirect to Stripe Checkout for payment
      await createCheckoutMutation.mutateAsync({
        teamId: team.id,
        projectId: project.id,
        paymentType: 'development',
        quoteId: quote.id,
      })
      // Note: useCreateCheckout handles the redirect to Stripe
    } catch (error) {
      toast.error(t('common.error'))
    }
  }

  const handleRejectQuote = async (reason: string) => {
    if (!quote) return

    try {
      await rejectQuoteMutation.mutateAsync({ quoteId: quote.id, reason })
      setIsRejectModalOpen(false)
      toast.success(t('quoting.quoteRejected'))
    } catch (error) {
      toast.error(t('common.error'))
    }
  }

  const handlePayNow = async () => {
    if (!quote) return

    try {
      await createCheckoutMutation.mutateAsync({
        teamId: team.id,
        projectId: project.id,
        paymentType: 'development',
        quoteId: quote.id,
      })
      // Note: useCreateCheckout handles the redirect to Stripe
    } catch (error) {
      toast.error(t('common.error'))
    }
  }

  const getQuoteStatusBanner = (quote: Quote) => {
    if (quote.status === 'accepted') {
      return (
        <div className="flex items-center justify-between gap-4 rounded-xl bg-green-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100">
              <Check className="h-[18px] w-[18px] text-green-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-green-900">
                {t('quoting.quoteAccepted')}
              </h2>
              <p className="mt-0.5 text-sm text-green-700">
                {t('quoting.quoteAcceptedDescription')}
              </p>
            </div>
          </div>
          <button
            onClick={handlePayNow}
            disabled={createCheckoutMutation.isPending}
            className="shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            {createCheckoutMutation.isPending ? t('common.loading') : t('quoting.payNow')}
          </button>
        </div>
      )
    }

    if (quote.status === 'rejected') {
      return (
        <div className="flex items-start gap-3 rounded-xl bg-amber-50 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <AlertCircle className="h-[18px] w-[18px] text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-amber-900">
              {t('quoting.quoteRejected')}
            </h2>
            <p className="mt-0.5 text-sm text-amber-700">
              {t('quoting.quoteRejectedDescription')}
            </p>
          </div>
        </div>
      )
    }

    if (quote.status === 'expired') {
      return (
        <div className="flex items-start gap-3 rounded-xl bg-slate-100 p-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200">
            <Clock className="h-[18px] w-[18px] text-slate-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              {t('quoting.quoteExpired')}
            </h2>
            <p className="mt-0.5 text-sm text-slate-700">
              {t('quoting.quoteExpiredDescription')}
            </p>
          </div>
        </div>
      )
    }

    // Pending status - default banner
    return (
      <div className="flex flex-col gap-4 rounded-xl bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
            <Receipt className="h-[18px] w-[18px] text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-amber-900">
              {t('quoting.welcomeTitle')}
            </h2>
            <p className="mt-0.5 text-sm text-amber-700">
              {t('quoting.welcomeDescription')}
            </p>
          </div>
        </div>
        {/* Download specs button - centered and highlighted */}
        {project.specs_content && (
          <div className="flex justify-center">
            <button
              onClick={() => navigate(`/p/${project.short_id}/specs/print`)}
              className="flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-accent/90"
            >
              <Download className="h-4 w-4" />
              {t('quoting.downloadSpecsButton')}
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />

      <main className="flex flex-1 flex-col overflow-hidden bg-background-secondary">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-8 py-6">
          <div className="flex items-center gap-4">
            {/* Back button */}
            <button
              onClick={() => navigate('/dashboard')}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-white text-foreground transition-colors hover:bg-background-secondary"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Title group */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {team.name}
              </span>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-foreground">
                  {project.name}
                </h1>
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                  {t('team.status.quoting')}
                </span>
              </div>
            </div>
          </div>

          {/* Right side: Print and Download buttons */}
          <div className="flex items-center gap-3">
            {/* Print specs button - visible when scoping is completed */}
            {project.specs_content && (
              <button
                onClick={() => navigate(`/p/${project.short_id}/specs/print`)}
                className="flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
              >
                <Printer className="h-4 w-4" />
                {t('scoping.printSpecs')}
              </button>
            )}

            {project.specs_pdf_url && (
              <button
                onClick={handleDownloadPdf}
                className="flex h-10 items-center gap-2 rounded-lg border border-border bg-white px-4 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
              >
                <Download className="h-4 w-4" />
                {t('quoting.downloadSpecs')}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col gap-5 overflow-hidden px-8 pb-6">
          {/* Horizontal Stepper */}
          <div className="flex items-center">
            <HorizontalStep
              number={1}
              label={t('quoting.step1Short')}
              status="completed"
            />
            <HorizontalStep
              number={2}
              label={t('quoting.step2Short')}
              status="completed"
            />
            <HorizontalStep
              number={3}
              label={t('quoting.step3Short')}
              status="current"
            />
            <HorizontalStep
              number={4}
              label={t('quoting.step4Short')}
              status="upcoming"
              isLast
            />
          </div>

          {/* Phase Info Banner */}
          {quote ? getQuoteStatusBanner(quote) : (
            <div className="flex flex-col gap-4 rounded-xl bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100">
                  <Receipt className="h-[18px] w-[18px] text-amber-600" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-amber-900">
                    {t('quoting.welcomeTitle')}
                  </h2>
                  <p className="mt-0.5 text-sm text-amber-700">
                    {t('quoting.welcomeDescription')}
                  </p>
                </div>
              </div>
              {/* Download specs button - centered and highlighted */}
              {project.specs_content && (
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate(`/p/${project.short_id}/specs/print`)}
                    className="flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-accent/90"
                  >
                    <Download className="h-4 w-4" />
                    {t('quoting.downloadSpecsButton')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Two Columns */}
          <div className="flex flex-1 gap-6 overflow-hidden">
            {/* Left Column - Quote */}
            <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background">
              {/* Quote Header */}
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <div className="flex items-center gap-2">
                  <Receipt className="h-[18px] w-[18px] text-foreground" />
                  <span className="text-[15px] font-semibold text-foreground">
                    {t('quoting.quoteTitle')}
                  </span>
                </div>
              </div>

              {/* Quote Content */}
              <div className="flex-1 overflow-auto p-6">
                {isLoadingQuote ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                  </div>
                ) : quote ? (
                  <div className="space-y-6">
                    {/* Amount Card */}
                    <div className="rounded-lg bg-background-secondary p-6 text-center">
                      <p className="text-sm text-foreground-muted">{t('quoting.amount')}</p>
                      <p className="mt-1 text-3xl font-bold text-foreground">
                        {formatCurrency(quote.amount, quote.currency)}
                      </p>
                      <p className="mt-1 text-xs text-foreground-muted uppercase">
                        {quote.currency} • HT
                      </p>
                    </div>

                    {/* Quote Details */}
                    <div className="space-y-4">
                      {quote.estimated_duration && (
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-secondary">
                            <Clock className="h-4 w-4 text-foreground-muted" />
                          </div>
                          <div>
                            <p className="text-xs text-foreground-muted">{t('quoting.estimatedDuration')}</p>
                            <p className="text-sm font-medium text-foreground">{quote.estimated_duration}</p>
                          </div>
                        </div>
                      )}

                      {quote.valid_until && (
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-secondary">
                            <Calendar className="h-4 w-4 text-foreground-muted" />
                          </div>
                          <div>
                            <p className="text-xs text-foreground-muted">{t('quoting.validUntil')}</p>
                            <p className="text-sm font-medium text-foreground">{formatDate(quote.valid_until)}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quote Description */}
                    {quote.description && (
                      <div className="border-t border-border pt-6">
                        <h3 className="text-sm font-semibold text-foreground">{t('quoting.quoteDetails')}</h3>
                        <div className="prose prose-sm mt-3 max-w-none prose-headings:text-foreground prose-p:text-foreground-muted prose-ul:text-foreground-muted prose-li:text-foreground-muted">
                          <ReactMarkdown>{quote.description}</ReactMarkdown>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {quote.status === 'pending' && (
                      <div className="flex gap-3 border-t border-border pt-6">
                        <button
                          onClick={() => setIsRejectModalOpen(true)}
                          className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-background-secondary"
                        >
                          {t('quoting.requestChanges')}
                        </button>
                        <button
                          onClick={() => setIsAcceptModalOpen(true)}
                          className="flex-1 rounded-lg bg-accent px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-accent/90"
                        >
                          {t('quoting.acceptQuote')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
                      <Receipt className="h-8 w-8 text-foreground-muted" />
                    </div>
                    <h3 className="mt-4 text-sm font-medium text-foreground">
                      {t('quoting.noQuoteYet')}
                    </h3>
                    <p className="mt-1 max-w-sm text-sm text-foreground-muted">
                      {t('quoting.noQuoteDescription')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Discussion */}
            <div className="flex w-[380px] shrink-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
              {/* Discussion Header */}
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-[18px] w-[18px] text-foreground" />
                  <span className="text-[15px] font-semibold text-foreground">
                    {t('quoting.discussionTitle')}
                  </span>
                </div>
              </div>

              {/* Exchange Zone */}
              <div className="flex-1 overflow-hidden">
                <TeamExchangeZone
                  teamId={team.id}
                  subtitle=""
                  embedded
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Accept Quote Confirmation Modal */}
      <ConfirmModal
        isOpen={isAcceptModalOpen}
        onClose={() => setIsAcceptModalOpen(false)}
        onConfirm={handleAcceptQuote}
        isLoading={acceptQuoteMutation.isPending}
        title={t('quoting.acceptConfirmTitle')}
        description={t('quoting.acceptConfirmDescription')}
        confirmLabel={t('quoting.acceptQuote')}
      />

      {/* Reject Quote Modal */}
      <RejectQuoteModal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onConfirm={handleRejectQuote}
        isLoading={rejectQuoteMutation.isPending}
      />
    </div>
  )
}
